'use client';
import { useEffect, useMemo, useState } from 'react';
import { LuActivity, LuGauge, LuPlus, LuSettings2, LuShield, LuShieldCheck, LuSparkles, LuUsers } from 'react-icons/lu';
import type { Role } from '@prisma/client';
import { Badge } from '@/core/ui/primitives/badge';
import { Button } from '@/core/ui/primitives/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/core/ui/primitives/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/core/ui/primitives/dialog';
import { Input } from '@/core/ui/primitives/input';
import { Separator } from '@/core/ui/primitives/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/core/ui/primitives/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/core/ui/primitives/tabs';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { createSecurityProfile, updateSecurityRolePrivileges } from '../actions';
import { ProfileCard } from './ProfileCard';
import { PrivilegeAccordion } from './PrivilegeAccordion';
interface Privilege {
  id: string;
  scope: string;
  feature: string;
  description: string;
}
interface Profile {
  id: string;
  name: string;
  privileges: string[];
}
interface RoleEntry {
  role: Role;
  privileges: string[];
}
interface SecurityAdminViewProps {
  profiles: Profile[];
  privileges: Privilege[];
  groupedPrivileges: Record<string, Privilege[]>;
  enabledFeatures: Record<string, boolean>;
  roles: RoleEntry[];
  roleDefaults: Record<Role, string[]>;
  /** Called after successful mutations to reload data */
  onRefresh?: () => void;
}
function formatLabel(label: string): string {
  return label.slice(0, 1).toUpperCase() + label.slice(1);
}
function computeIsCustomized(entry: RoleEntry, roleDefaults: Record<Role, string[]>): boolean {
  const defaults = roleDefaults[entry.role] ?? [];
  if (defaults.length !== entry.privileges.length) {
    return true;
  }
  const defaultSet = new Set(defaults);
  const privilegeSet = new Set(entry.privileges);
  for (const id of defaultSet) {
    if (!privilegeSet.has(id)) return true;
  }
  for (const id of privilegeSet) {
    if (!defaultSet.has(id)) return true;
  }
  return false;
}
export function SecurityAdminView({
  profiles,
  privileges,
  groupedPrivileges,
  enabledFeatures,
  roles,
  roleDefaults,
  onRefresh,
}: SecurityAdminViewProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [activeRole, setActiveRole] = useState<Role | null>(null);
  const [createSelection, setCreateSelection] = useState<Set<string>>(() => new Set());
  const [roleSelection, setRoleSelection] = useState<Set<string>>(() => new Set());
  const privilegeLookup = useMemo(() => {
    const map: Record<string, Privilege> = {};
    privileges.forEach((privilege) => {
      map[privilege.id] = privilege;
    });
    return map;
  }, [privileges]);
  const disabledFeatures = Object.entries(enabledFeatures)
    .filter(([, enabled]) => !enabled)
    .map(([feature]) => feature);
  const customizedRoles = roles.filter((entry) => computeIsCustomized(entry, roleDefaults)).length;
  const activeRoleEntry = roles.find((entry) => entry.role === activeRole);
  const activeRoleDefaults = activeRole ? roleDefaults[activeRole] ?? [] : [];
  useEffect(() => {
    if (activeRoleEntry) {
      setRoleSelection(new Set(activeRoleEntry.privileges));
    } else {
      setRoleSelection(new Set());
    }
  }, [activeRoleEntry]);
  useEffect(() => {
    if (!createOpen) {
      setCreateSelection(new Set());
    }
  }, [createOpen]);
  const featureSpread = (ids: string[]) => {
    const set = new Set<string>();
    ids.forEach((id) => {
      const privilege = privilegeLookup[id];
      if (privilege?.feature) {
        set.add(privilege.feature);
      }
    });
    return set.size;
  };
  const handleCreateToggle = (privilegeId: string, next: boolean) => {
    setCreateSelection((prev) => {
      const nextSet = new Set(prev);
      if (next) {
        nextSet.add(privilegeId);
      } else {
        nextSet.delete(privilegeId);
      }
      return nextSet;
    });
  };
  const handleRoleToggle = (privilegeId: string, next: boolean) => {
    setRoleSelection((prev) => {
      const nextSet = new Set(prev);
      if (next) {
        nextSet.add(privilegeId);
      } else {
        nextSet.delete(privilegeId);
      }
      return nextSet;
    });
  };
  return (
    <div className='space-y-8'>
      <div className='rounded-2xl border bg-gradient-to-br from-primary/5 via-background to-background p-6 shadow-sm'>
        <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
          <div className='space-y-2'>
            <div className='inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary'>
              <LuShieldCheck className='h-4 w-4' />
              Security console
            </div>
            <h1 className='text-3xl font-bold tracking-tight'>Roles, profiles, and guardrails</h1>
            <p className='max-w-2xl text-sm text-muted-foreground'>
              Tune the base roles, adjust assignable profiles, and keep an eye on operational limits from a single
              console.
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size='lg' className='gap-2'>
                <LuPlus className='h-4 w-4' />
                New profile
              </Button>
            </DialogTrigger>
            <DialogContent className='max-w-2xl max-h-[85vh] flex flex-col'>
              <DialogHeader>
                <DialogTitle>Create security profile</DialogTitle>
                <DialogDescription>
                  Bundle privileges into a reusable plan. These profiles are assigned to users or organizations.
                </DialogDescription>
              </DialogHeader>

              <form
                id='create-profile-form'
                action={async (formData) => {
                  await createSecurityProfile(formData);
                  setCreateOpen(false);
                  onRefresh?.();
                }}
                className='flex-1 overflow-y-auto py-4 pr-2 space-y-6'>
                <div className='space-y-2'>
                  <label htmlFor='name' className='text-sm font-medium'>
                    Profile name
                  </label>
                  <Input
                    id='name'
                    name='name'
                    placeholder='e.g. Contractor, Auditor'
                    required
                    className='bg-muted/40'
                  />
                </div>

                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <h3 className='text-sm font-medium text-muted-foreground uppercase tracking-wider'>Privileges</h3>
                    <Badge variant='outline' className='text-[10px]'>
                      {privileges.length} total
                    </Badge>
                  </div>
                  <PrivilegeAccordion
                    groupedPrivileges={groupedPrivileges}
                    enabledFeatures={enabledFeatures}
                    selected={createSelection}
                    onToggle={handleCreateToggle}
                  />
                  {Array.from(createSelection).map((id) => (
                    <input key={id} type='hidden' name='privileges' value={id} />
                  ))}
                </div>
              </form>

              <DialogFooter className='pt-4 border-t'>
                <Button variant='outline' onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type='submit' form='create-profile-form'>
                  Create profile
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className='mt-6 grid gap-4 md:grid-cols-3'>
          <Card className='border-primary/20 bg-primary/5'>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>Profiles ready</CardTitle>
              <span className='rounded-full bg-primary/10 p-2 text-primary'>
                <LuUsers className='h-4 w-4' />
              </span>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='text-3xl font-bold'>{profiles.length}</div>
              <p className='text-xs text-muted-foreground'>Assignable plans</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>Base roles</CardTitle>
              <span className='rounded-full bg-primary/10 p-2 text-primary'>
                <LuShield className='h-4 w-4' />
              </span>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='text-3xl font-bold'>{roles.length}</div>
              <p className='text-xs text-muted-foreground'>
                {customizedRoles > 0 ? `${customizedRoles} customized` : 'Using defaults'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>Feature coverage</CardTitle>
              <span className='rounded-full bg-primary/10 p-2 text-primary'>
                <LuActivity className='h-4 w-4' />
              </span>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='text-3xl font-bold'>{Object.keys(enabledFeatures).length}</div>
              <p className='text-xs text-muted-foreground'>
                {disabledFeatures.length > 0 ? `${disabledFeatures.length} disabled` : 'All enabled'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue='profiles' className='space-y-6'>
        <TabsList className='bg-muted/40 p-1 rounded-xl'>
          <TabsTrigger value='profiles'>Profiles</TabsTrigger>
          <TabsTrigger value='roles'>Roles</TabsTrigger>
          <TabsTrigger value='controls'>Admin controls</TabsTrigger>
        </TabsList>

        <TabsContent value='profiles' className='space-y-4'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='space-y-1'>
              <h2 className='text-xl font-semibold'>Assignable profiles</h2>
              <p className='text-sm text-muted-foreground'>
                Templates you can attach to users or organizations. Privileges cascade to vault access.
              </p>
            </div>
            <Badge variant='outline' className='self-start'>
              {privileges.length} privileges registered
            </Badge>
          </div>

          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
            {profiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                groupedPrivileges={groupedPrivileges}
                enabledFeatures={enabledFeatures}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value='roles' className='space-y-4'>
          <Card>
            <CardHeader className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
              <div className='space-y-1'>
                <CardTitle className='text-xl'>Base roles</CardTitle>
                <CardDescription>
                  These ship with the product and provide the baseline for vault and org permissions. Override
                  cautiously.
                </CardDescription>
              </div>
              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <Badge variant='outline'>Default matrix stored in DB</Badge>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='overflow-hidden rounded-xl border'>
                <Table className='text-sm'>
                  <TableHeader>
                    <TableRow>
                      <TableHead className='w-1/3'>Role</TableHead>
                      <TableHead>Privileges</TableHead>
                      <TableHead className='w-1/4 text-right'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.map((entry) => {
                      const selectedSet = new Set(entry.privileges);
                      const featureCount = featureSpread(entry.privileges);
                      const hasDisabledFeature = entry.privileges.some((id) => {
                        const privilege = privilegeLookup[id];
                        return privilege?.feature && !enabledFeatures[privilege.feature];
                      });
                      const customized = computeIsCustomized(entry, roleDefaults);
                      return (
                        <TableRow key={entry.role} className='hover:bg-primary/5'>
                          <TableCell>
                            <div className='flex items-center gap-3'>
                              <div className='rounded-full bg-primary/10 p-2 text-primary'>
                                <LuShield className='h-4 w-4' />
                              </div>
                              <div>
                                <div className='font-semibold capitalize'>{entry.role}</div>
                                <p className='text-xs text-muted-foreground'>{featureCount} features</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className='flex flex-wrap gap-2'>
                              {entry.privileges.slice(0, 10).map((id) => (
                                <Badge key={id} variant='secondary' className='px-2 text-[11px]'>
                                  {id}
                                </Badge>
                              ))}
                              {entry.privileges.length > 10 && (
                                <Badge variant='outline' className='px-2 text-[11px] text-muted-foreground'>
                                  +{entry.privileges.length - 10} more
                                </Badge>
                              )}
                              <Badge
                                variant='outline'
                                className={cn(
                                  'px-2 text-[11px]',
                                  customized ? 'text-primary border-primary/60' : 'text-muted-foreground'
                                )}>
                                {customized ? 'Customized' : 'Using defaults'}
                              </Badge>
                              {hasDisabledFeature && (
                                <Badge
                                  variant='outline'
                                  className='px-2 text-[11px] text-amber-700 border-amber-200 bg-amber-50'>
                                  Contains disabled feature
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='text-right'>
                            <Button variant='outline' size='sm' onClick={() => setActiveRole(entry.role)}>
                              Edit privileges
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <p className='text-xs text-muted-foreground'>
                Roles act as the floor for vault permissions. Profiles can elevate access but should not bypass
                mandatory controls.
              </p>
            </CardContent>
          </Card>

          <Dialog open={Boolean(activeRole)} onOpenChange={(open) => setActiveRole(open ? activeRole : null)}>
            <DialogContent className='max-w-3xl max-h-[85vh] flex flex-col'>
              {activeRoleEntry ? (
                <>
                  <DialogHeader className='pb-4'>
                    <DialogTitle className='flex items-center gap-2 text-2xl'>
                      <LuShield className='h-6 w-6 text-primary' />
                      {formatLabel(activeRoleEntry.role)}
                    </DialogTitle>
                    <DialogDescription>
                      Adjust the base privilege matrix for this role. Changes apply globally and override defaults
                      stored in the database.
                    </DialogDescription>
                    <div className='flex flex-wrap gap-2 text-xs'>
                      <Badge variant='secondary' className='px-2 text-[11px]'>
                        {roleSelection.size} privileges selected
                      </Badge>
                      <Badge variant='outline' className='px-2 text-[11px]'>
                        Default: {activeRoleDefaults.length}
                      </Badge>
                    </div>
                  </DialogHeader>

                  <div className='max-h-[60vh] overflow-y-auto pr-2 -mr-2 space-y-4'>
                    <form
                      id={`role-form-${activeRoleEntry.role}`}
                      action={async (formData) => {
                        await updateSecurityRolePrivileges(formData);
                        setActiveRole(null);
                        onRefresh?.();
                      }}
                      className='space-y-4'>
                      <input type='hidden' name='role' value={activeRoleEntry.role} />
                      {Array.from(roleSelection).map((id) => (
                        <input key={id} type='hidden' name='privileges' value={id} />
                      ))}
                      <PrivilegeAccordion
                        groupedPrivileges={groupedPrivileges}
                        enabledFeatures={enabledFeatures}
                        selected={roleSelection}
                        onToggle={handleRoleToggle}
                      />
                    </form>
                  </div>

                  <DialogFooter className='mt-auto border-t pt-6'>
                    <Button variant='outline' onClick={() => setActiveRole(null)}>
                      Cancel
                    </Button>
                    <Button type='submit' form={`role-form-${activeRoleEntry.role}`}>
                      Save changes
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <div className='flex h-full flex-col items-center justify-center gap-3 text-muted-foreground'>
                  <LuSettings2 className='h-6 w-6' />
                  <p className='text-sm'>Select a role to edit its privileges.</p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value='controls' className='space-y-4'>
          <div className='space-y-1'>
            <h2 className='text-xl font-semibold'>Admin console guardrails</h2>
            <p className='text-sm text-muted-foreground'>
              Limits such as AI credits or transcription minutes are managed in the admin console. Surface them here for
              quick context.
            </p>
          </div>

          <div className='grid gap-4 md:grid-cols-2'>
            <Card>
              <CardHeader className='flex flex-row items-start justify-between'>
                <div>
                  <CardTitle className='text-lg'>Usage guardrails</CardTitle>
                  <CardDescription>Read-only mirrors of spend and quota caps set elsewhere.</CardDescription>
                </div>
                <Badge variant='outline' className='gap-1'>
                  <LuGauge className='h-3.5 w-3.5' />
                  Console-managed
                </Badge>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>Monthly AI credits</label>
                  <Input value='Managed in admin console' readOnly className='bg-muted/40' />
                  <p className='text-xs text-muted-foreground'>
                    Keep this in sync with the ops console so stakeholders see the same ceiling.
                  </p>
                </div>
                <Separator />
                <div className='space-y-2'>
                  <label className='text-sm font-medium'>Transcription minutes</label>
                  <Input value='Managed in admin console' readOnly className='bg-muted/40' />
                  <p className='text-xs text-muted-foreground'>
                    Applies to audio capture and transcript generation across all vaults.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='flex flex-row items-start justify-between'>
                <div>
                  <CardTitle className='text-lg'>Operational toggles</CardTitle>
                  <CardDescription>Feature gates and emergency brakes surfaced for admins.</CardDescription>
                </div>
                <Badge variant='secondary' className='gap-1'>
                  <LuSparkles className='h-3.5 w-3.5' />
                  Coming from console
                </Badge>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium'>AI automation</span>
                    <Badge variant='outline' className='text-[11px]'>
                      External
                    </Badge>
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    Whether automated AI actions are allowed. Wire this toggle to the admin console flag.
                  </p>
                </div>
                <Separator />
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium'>Vault export</span>
                    <Badge variant='outline' className='text-[11px]'>
                      External
                    </Badge>
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    Gate data exports during incidents. Mirror the state from the admin console for visibility.
                  </p>
                </div>
                <Separator />
                <div className='rounded-lg border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground'>
                  Add more console-backed controls here when they become available. This page stays the single surface
                  for admins.
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
